'use client';

/**
 * AddStopBetween — put a stop between two stops you already have.
 *
 * The trigger is the "+" on StopList's timeline, on every leg (LegConnector).
 * It opens this inline box right there: a search, and before you type, a few
 * ideas on that very drive. The stop lands at exactly that position, no
 * "least detour" guessing.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import AddStopByPlace from '../AddStopByPlace';
import type { ResolvedLocation } from '../LocationAutocomplete';
import type { Anchor } from '../../utils/placeSuggest';
import { formatAddedKm, withRoadDistance, type PlacementInfo } from '../../utils/tripInsert';
import { nearestOnPath, pointAlongRoute } from '../../utils/routeSnap';
import { searchEntitiesByBounds } from '../../lib/searchEntities';
import { boxesForLeg, ideasForLeg, type Box, type LegPoint, type RouteSuggestion } from './routeSuggest';
import type { Entity } from '../../types';

const PlusIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" {...props}>
    <path d="M8 3.5v9M3.5 8h9" />
  </svg>
);

/* ── Ideas on this drive ─────────────────────────────────────────────────── */

/** With a road line, ideas further off it than this aren't on this drive. */
const MAX_IDEA_OFF_ROUTE_KM = 25;

/* One bounds search per box, shared: the ladder warms these while you read
   the trip, so an opened "+" shows its ideas at once instead of skeletons. */
const boxCache = new Map<string, Promise<Entity[]>>();
function fetchBox(box: Box): Promise<Entity[]> {
  const key = [box.north, box.south, box.east, box.west].map((n) => n.toFixed(2)).join(',');
  const hit = boxCache.get(key);
  if (hit) return hit;
  const p = searchEntitiesByBounds({ bounds: box, zoom: 10, limit: 120 })
    .then((r) => r.entities ?? [])
    .catch(() => {
      boxCache.delete(key); // a failed look can be retried
      return [] as Entity[];
    });
  if (boxCache.size > 80) boxCache.delete(boxCache.keys().next().value as string);
  boxCache.set(key, p);
  return p;
}

const legBoxesFor = (from: LegPoint, to: LegPoint, insertAt: number, route?: Anchor[] | null) =>
  boxesForLeg(from, to, insertAt, (t) =>
    route && route.length >= 2 && typeof from.lat === 'number' && typeof from.lng === 'number' && typeof to.lat === 'number' && typeof to.lng === 'number'
      ? pointAlongRoute(route, { lat: from.lat, lng: from.lng }, { lat: to.lat, lng: to.lng }, t)
      : null
  );

/** Warm the ideas for each gap in the trip (at most `max` gaps). */
export function prefetchLegIdeas(stops: LegPoint[], route?: Anchor[] | null, max = 4): void {
  for (let i = 0; i < stops.length - 1 && i < max; i++) {
    legBoxesFor(stops[i], stops[i + 1], i + 1, route).forEach((b) => void fetchBox(b.box));
  }
}

function IdeaPhoto({ src }: { src: string }) {
  // A tent tile shows until the photo lands (then it fades in over it), and
  // stays if the photo never does: never a blank grey square.
  const [state, setState] = useState<'loading' | 'ready' | 'broken'>('loading');
  return (
    <span aria-hidden="true" className="relative flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-tp-sm bg-tp-day-6/10 text-tp-day-6">
      <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" className="h-[18px] w-[18px]">
        <path d="M10 3.5 2.5 16.5h15L10 3.5ZM10 16.5l-2.5-4.5h5L10 16.5Z" />
      </svg>
      {state !== 'broken' && !!src && (
        <img
          src={src}
          alt=""
          loading="eager"
          decoding="async"
          onLoad={() => setState('ready')}
          onError={() => setState('broken')}
          className={`absolute inset-0 h-full w-full object-cover ring-1 ring-inset ring-black/5 transition-opacity duration-200 ${
            state === 'ready' ? 'opacity-100' : 'opacity-0'
          }`}
        />
      )}
    </span>
  );
}

/**
 * Three or four places to stay on this one drive, in driving order, shown
 * before anything is typed. Each says what calling in adds ("On route",
 * "+12 km"). Renders nothing on a stretch with no photographed listings.
 */
function LegIdeas({
  from,
  to,
  insertAt,
  route,
  onTrip,
  placement,
  onPick,
}: {
  from: LegPoint;
  to: LegPoint;
  insertAt: number;
  route?: Anchor[] | null;
  onTrip: number[];
  placement?: (p: { lat: number; lng: number }) => PlacementInfo | null;
  onPick: (place: ResolvedLocation) => void;
}) {
  const routeKey = route && route.length >= 2 ? `${route.length}:${route[0].lat.toFixed(3)}` : '';
  const boxes = useMemo(
    () => legBoxesFor(from, to, insertAt, routeKey ? route : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [from.lat, from.lng, to.lat, to.lng, insertAt, routeKey]
  );
  const [ideas, setIdeas] = useState<RouteSuggestion[] | null>(boxes.length ? null : []);
  const onTripKey = onTrip.join(',');

  useEffect(() => {
    if (!boxes.length) {
      setIdeas([]);
      return;
    }
    let cancelled = false;
    setIdeas(null);
    Promise.all(boxes.map((b) => fetchBox(b.box))).then((results) => {
      if (cancelled) return;
      const keep = routeKey
        ? (pt: { lat: number; lng: number }) => (nearestOnPath(route!, pt)?.km ?? Infinity) <= MAX_IDEA_OFF_ROUTE_KM
        : undefined;
      setIdeas(ideasForLeg(results, boxes, new Set(onTrip), 4, keep));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, onTripKey]);

  if (ideas && ideas.length === 0) return null;

  return (
    <section className="mt-3" aria-label={`Ideas between ${from.name} and ${to.name}`}>
      <h5 className="m-0 mb-1 px-0.5 text-tp-xs font-medium text-tp-muted">On this drive</h5>
      <ul className="m-0 flex list-none flex-col p-0">
        {ideas === null
          ? [0, 1, 2].map((i) => (
              <li key={i} className="flex h-12 items-center gap-3 px-1" aria-hidden="true">
                <span className="h-9 w-9 flex-none animate-pulse rounded-tp-sm bg-tp-surface motion-reduce:animate-none" />
                <span className="flex-1 space-y-1.5">
                  <span className="block h-3 w-1/2 animate-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                  <span className="block h-2.5 w-1/3 animate-pulse rounded bg-tp-surface motion-reduce:animate-none" />
                </span>
              </li>
            ))
          : ideas.map((c) => {
              const offKm = routeKey ? nearestOnPath(route!, c)?.km : null;
              const info = withRoadDistance(placement?.({ lat: c.lat, lng: c.lng }) ?? null, offKm);
              const chip = info ? (info.onRoute ? 'On route' : formatAddedKm(info.addKm)) : null;
              return (
                <li key={c.key} className="motion-safe:animate-[tp-rise_180ms_ease-out]">
                  <button
                    type="button"
                    onClick={() => onPick({ label: c.name, lat: c.lat, lng: c.lng, entityId: c.entityId, kind: 'campsite' })}
                    aria-label={`Add ${c.name}${chip ? `, ${chip === 'On route' ? 'on the route' : `adds about ${chip.slice(1)}`}` : ''}`}
                    className="group/idea flex h-12 w-full min-h-0 min-w-0 items-center gap-3 rounded-tp-sm border-0 bg-transparent px-1 text-left transition-colors duration-150 hover:bg-tp-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
                  >
                    <IdeaPhoto src={c.image} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-tp-base font-medium text-tp-ink">{c.name}</span>
                      <span className="block truncate text-tp-xs text-tp-muted">{c.detail}</span>
                    </span>
                    {chip && (
                      <span
                        className={`flex h-6 flex-none items-center whitespace-nowrap rounded-full px-2 text-tp-xs font-medium tabular-nums ${
                          info?.onRoute ? 'bg-tp-ok-soft text-tp-ok' : 'bg-tp-surface text-tp-muted'
                        }`}
                      >
                        {chip}
                      </span>
                    )}
                    <span
                      aria-hidden="true"
                      className="flex h-7 w-7 flex-none items-center justify-center rounded-full border-[1.5px] border-solid border-tp-brand bg-white text-tp-brand transition-colors duration-150 [@media(hover:hover)]:group-hover/idea:bg-tp-brand [@media(hover:hover)]:group-hover/idea:text-white"
                    >
                      <PlusIcon className="h-3.5 w-3.5" />
                    </span>
                  </button>
                </li>
              );
            })}
      </ul>
    </section>
  );
}

/* ── The inline box ──────────────────────────────────────────────────────── */

interface PanelProps {
  index: number;
  from: LegPoint;
  to: LegPoint;
  near: Anchor[];
  route?: Anchor[] | null;
  /** Listings already on the trip (never suggested again). */
  onTrip?: number[];
  /** What a pick here adds to the drive, for each row's chip. */
  placement?: (p: { lat: number; lng: number }) => PlacementInfo | null;
  onAdd: (place: ResolvedLocation, insertAt: number) => void;
  onClose: () => void;
  /** Under the ideas: every camp on this drive (a link to the full list). */
  extra?: React.ReactNode;
}

export function AddStopBetweenPanel({ index, from, to, near, route, onTrip = [], placement, onAdd, onClose, extra }: PanelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [searching, setSearching] = useState(false);
  // Opening low in a scrolled panel used to leave the box half under the
  // fold. Bring it (and room for the ideas under it) into view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const id = window.requestAnimationFrame(() => {
      // Scroll only the nearest scrolling box (the planner on desktop, the
      // results column on phones), never the page around it.
      let box: HTMLElement | null = el.parentElement;
      while (box && !(box.scrollHeight > box.clientHeight && /(auto|scroll)/.test(getComputedStyle(box).overflowY))) {
        box = box.parentElement;
      }
      if (box) {
        const r = el.getBoundingClientRect();
        const b = box.getBoundingClientRect();
        // Leave room under the box for the suggestion list.
        const wantBottom = r.bottom + 220;
        // 56px: clear of the phone sheet's pinned trip bar (and on desktop,
        // the row above stays in sight, so it reads as "between").
        const topGap = 56;
        if (r.top < b.top + topGap || wantBottom > b.bottom) {
          const delta = Math.min(r.top - b.top - topGap, wantBottom - b.bottom);
          box.scrollBy({ top: delta > 0 ? delta : r.top - b.top - topGap, behavior: reduce ? 'auto' : 'smooth' });
        }
      } else {
        el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      }
      el.querySelector<HTMLInputElement>('input[role="combobox"]')?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, []);
  return (
    <div
      ref={ref}
      className="relative my-0.5 rounded-tp-lg border border-solid border-tp-brand/25 bg-white p-3 shadow-tp-card motion-safe:animate-[tp-rise_160ms_ease-out]"
      data-testid={`add-stop-between-panel-${index}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="m-0 min-w-0 truncate text-tp-sm font-medium text-tp-muted">
          Between <span className="font-semibold text-tp-ink">{from.name}</span> and{' '}
          <span className="font-semibold text-tp-ink">{to.name}</span>
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cancel adding a stop here"
          className="flex h-8 w-8 min-h-0 min-w-0 flex-none items-center justify-center rounded-tp-sm border-0 bg-transparent text-tp-muted transition-colors duration-150 hover:bg-tp-surface hover:text-tp-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tp-brand/45"
        >
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" className="h-4 w-4" aria-hidden="true">
            <path d="M6 6l8 8M14 6l-8 8" />
          </svg>
        </button>
      </div>
      <AddStopByPlace
        insertAt={index + 1}
        onAdd={(place, at) => onAdd(place, at ?? index + 1)}
        allowMyLocation={false}
        near={near}
        route={route}
        placement={placement}
        autoFocus
        onEscape={onClose}
        onOpenChange={setSearching}
        ariaLabel={`Add a stop between ${from.name} and ${to.name}`}
        placeholder="Town, park or campsite"
      />
      {/* Before typing: a few places on this very drive. */}
      <div className={searching ? 'invisible' : ''} aria-hidden={searching || undefined}>
        <LegIdeas
          from={from}
          to={to}
          insertAt={index + 1}
          route={route}
          onTrip={onTrip}
          placement={placement}
          onPick={(place) => onAdd(place, index + 1)}
        />
        {extra}
      </div>
    </div>
  );
}
